import { Coordinate, MeasurementResult } from './flightUtils';
import { getChannelConfig, transformChannelValue, SENSOR_CHANNELS, GPS_ALTITUDE_KEY } from './sensorUtils';

export interface FlightMeasurementRow {
  tsMs: number;
  coord?: Coordinate;
  meas: Record<string, number>;
}

export interface BuildFlightMeasurementRowsResult {
  rows: FlightMeasurementRow[];
  channels: string[];
}

/**
 * Buckets coordinates and sensor measurements into second-by-second rows
 * between flight start and end.
 * If two or more datums are recorded for a second, the chronologically first one is used.
 * No averaging or interpolation is applied.
 */
export function buildFlightMeasurementRows(
  dateStart: string,
  dateEnd: string | undefined,
  coordinates?: Coordinate[],
  measurements: MeasurementResult[] = []
): BuildFlightMeasurementRowsResult {
  const startMs = new Date(dateStart).getTime();
  let endMs = startMs;

  if (dateEnd) {
    endMs = new Date(dateEnd).getTime();
  } else {
    let maxTime = startMs;
    if (coordinates && coordinates.length > 0) {
      maxTime = Math.max(maxTime, new Date(coordinates[coordinates.length - 1].timestamp).getTime());
    }
    measurements.forEach(m => {
      maxTime = Math.max(maxTime, new Date(m.timestamp).getTime());
    });
    endMs = maxTime;
  }

  if (endMs < startMs) {
    endMs = startMs;
  }

  const startSec = Math.floor(startMs / 1000) * 1000;
  const endSec = Math.floor(endMs / 1000) * 1000;

  // Sort coordinates chronologically to guarantee the first datum per second is picked
  const sortedCoords = coordinates ? [...coordinates].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) : [];
  const coordMap = new Map<number, Coordinate>();
  for (const c of sortedCoords) {
    const ts = Math.floor(new Date(c.timestamp).getTime() / 1000) * 1000;
    if (!coordMap.has(ts)) {
      coordMap.set(ts, c);
    }
  }

  // Sort measurements chronologically to guarantee the first datum per second is picked
  const sortedMeas = [...measurements].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const measMap = new Map<number, Record<string, number>>();
  const rawChannels = new Set<string>();

  for (const m of sortedMeas) {
    if (m.channel === GPS_ALTITUDE_KEY) continue;
    rawChannels.add(m.channel);
    const ts = Math.floor(new Date(m.timestamp).getTime() / 1000) * 1000;
    if (!measMap.has(ts)) {
      measMap.set(ts, {});
    }
    const existing = measMap.get(ts)!;
    if (existing[m.channel] === undefined) {
      existing[m.channel] = m.value;
    }
  }

  // Sort channels predictably: standard SENSOR_CHANNELS order first, then any extra channels alphabetically
  const standardOrder = Object.keys(SENSOR_CHANNELS).filter(k => k !== GPS_ALTITUDE_KEY);
  const channels: string[] = [];
  standardOrder.forEach(ch => {
    if (rawChannels.has(ch)) channels.push(ch);
  });
  Array.from(rawChannels)
    .filter(ch => !standardOrder.includes(ch))
    .sort()
    .forEach(ch => channels.push(ch));

  const rows: FlightMeasurementRow[] = [];
  for (let t = startSec; t <= endSec; t += 1000) {
    rows.push({
      tsMs: t,
      coord: coordMap.get(t),
      meas: measMap.get(t) || {}
    });
  }

  return { rows, channels };
}

function escapeCsvField(value: any): string {
  if (value === undefined || value === null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export interface GenerateCsvOptions {
  aircraftRegistration: string;
  dateStart: string;
  dateEnd?: string;
  coordinates?: Coordinate[];
  measurements?: MeasurementResult[];
}

/**
 * Generates CSV content adhering to standard technical English headers and ISO 8601 timestamps.
 * Schema: aircraftRegistration, timestamp, latitude, longitude, altitude, <channelKeys...>
 */
export function generateFlightTrackCsv(options: GenerateCsvOptions): string {
  const { aircraftRegistration, dateStart, dateEnd, coordinates, measurements = [] } = options;
  const { rows, channels } = buildFlightMeasurementRows(dateStart, dateEnd, coordinates, measurements);

  const headers = [
    'aircraftRegistration',
    'timestamp',
    'latitude',
    'longitude',
    'altitude',
    ...channels,
  ];

  const csvRows: string[] = [];
  csvRows.push(headers.map(escapeCsvField).join(','));

  for (const row of rows) {
    const tsIso = new Date(row.tsMs).toISOString();
    const latStr = row.coord ? row.coord.latitude.toFixed(5) : '';
    const lonStr = row.coord ? row.coord.longitude.toFixed(5) : '';
    const altStr = row.coord ? row.coord.altitude.toFixed(0) : '';

    const channelValues = channels.map(ch => {
      const rawVal = row.meas[ch];
      if (rawVal === undefined || rawVal === null || isNaN(rawVal)) {
        return '';
      }
      const transformed = transformChannelValue(ch, rawVal);
      if (transformed === undefined) return '';
      const config = getChannelConfig(ch);
      return transformed.toFixed(config.decimals);
    });

    const rowValues = [
      escapeCsvField(aircraftRegistration),
      escapeCsvField(tsIso),
      escapeCsvField(latStr),
      escapeCsvField(lonStr),
      escapeCsvField(altStr),
      ...channelValues.map(escapeCsvField),
    ];

    csvRows.push(rowValues.join(','));
  }

  return csvRows.join('\r\n');
}

/**
 * Triggers a file download of the CSV content in the browser.
 */
export function downloadCsv(filename: string, csvContent: string): void {
  const bom = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
